import "@progress/kendo-ui/js/kendo.treeview.js";
import "@progress/kendo-ui/js/kendo.upload";
import * as ko from "knockout";
import * as Constants from "../common/constants";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as ReportData from "../data/reportData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

interface IViewModel {
    reports: KnockoutObservableArray<ReportData.IReportDefinition>;
    newFolder: KnockoutObservable<string>;
    reportFolders: KnockoutObservableArray<string>;
    errors?: KnockoutValidationErrors;
    ID: KnockoutObservable<number>;
    Name: KnockoutObservable<string>;
    FileName: KnockoutObservable<string>;
    FolderName: KnockoutObservable<string>;
    FileKey: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    EmailSubjectTemplate: KnockoutObservable<string>;
    selectedReport: KnockoutComputed<boolean>;
    reportDownloadUrl: KnockoutObservable<string>;
    reportDesignerDownloadUrl: KnockoutObservable<string>;
    createNewFolder: () => void;
    createNewReport: () => void;
    saveReport: () => void;
    deleteReport: () => void;
    downloadReport: () => void;
    isValid: () => boolean;
    IsNameUnique: KnockoutObservable<boolean>;
}

export class ManageReportsController implements IController {

    private container: JQuery;
    private viewModel: IViewModel;
    private treeView: any;
    private treeDataSource: ReportData.IReportTreeItem[];
    private uploadControl: kendo.ui.Upload;
    private fieldsOnLoad: string;
    constructor() {
        const app: IApplication = Global.app();
        this.viewModel = {
            reports: ko.observableArray<ReportData.IReportDefinition>(),
            newFolder: ko.observable<string>(),
            reportFolders: ko.observableArray<string>(),
            ID: ko.observable<number>(-1),
            Name: ko.observable<string>("").extend({ required: true }),
            FileName: ko.observable<string>(""),
            FolderName: ko.observable<string>(""),
            FileKey: ko.observable<string>(""),
            IsActive: ko.observable<boolean>(true),
            IsNameUnique: ko.observable<boolean>(true),
            EmailSubjectTemplate: ko.observable<string>(""),
            reportDesignerDownloadUrl: ko.observable<string>(app.webServiceBaseURL + "/api/report/downloadreportdesigner"),
            reportDownloadUrl: ko.observable<string>(),
            selectedReport: ko.pureComputed((): boolean => {
                return this.viewModel.ID() >= 0;
            }),
            createNewFolder: () => {
                if (this.viewModel.newFolder()) {
                    this.createTreeviewFolder(this.viewModel.newFolder());
                    this.viewModel.newFolder("");
                }
            },
            createNewReport: () => {
                this.treeView.select($());
                this.viewModel.IsNameUnique(true);
                this.bindReportToViewModel({ ID: 0, Name: "", FolderName: "", FileKey: "", FileName: "", EmailSubjectTemplate: "", IsActive: true });
            },
            saveReport: () => {
               if (this.viewModel.isValid()) {
                    const waitIndicator = UI.showWaitIndicator(this.container);

                    ReportData.SaveReport(ko.toJS(this.viewModel) as any)
                        .done((saveResponse: Data.IResponse<number>) => {
                            if (saveResponse.Success) {
                                this.viewModel.IsNameUnique(true);
                                // if there is a pending upload, go ahead and upload the file now so the user doesn't lose it
                                if (this.uploadControl.getFiles().length > 0) {
                                  this.uploadControl.upload();
                                } else {
                                  this.loadReports(this.viewModel.Name());
                                }
                             } else {
                                 UI.showErrorMessage(saveResponse.ErrorMessages);
                        }
                        })
                        .always(() => {
                            UI.hideWaitIndicator(this.container, waitIndicator);
                        });
                }
            },
            downloadReport: () => {
                this.container.find("form[name='downloadReportForm']").submit();
            },
            deleteReport: () => {
                UI.showConfirmDialog("Are you sure you want to delete this report?",
                    () => {
                        const waitIndicator = UI.showWaitIndicator(this.container);
                        this.viewModel.IsActive(false);
                        ReportData.SaveReport(ko.toJS(this.viewModel) as any)
                            .done((response: Data.IResponse<number>) => {
                                this.loadReports();
                            }).always(() => {
                                UI.hideWaitIndicator(this.container, waitIndicator);
                            });
                    });
            },
            isValid: (): boolean => {
                 // Check for unique Name
                const reportNames = this.viewModel.reports().filter((x) => x.ID !== this.viewModel.ID()).map((x) => x.Name.toLowerCase());
                if (reportNames.indexOf(this.viewModel.Name().toLowerCase()) > -1) {
                  this.viewModel.IsNameUnique(false);
                  UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
                  return false;
                }
                if (this.viewModel.errors && this.viewModel.errors().length > 0) {
                    this.viewModel.errors.showAllMessages();
                    UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
                    return false;
                }
                return true;
            },

        };
        this.viewModel.errors = ko.validation.group(this.viewModel, { deep: true });
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const app: IApplication = Global.app();
        const me: ManageReportsController = this;

        this.container = container;

        ko.applyBindings(this.viewModel, container[0]);

        this.uploadControl = this.container.find(".uploadFiles").kendoUpload({
            async: {
                saveUrl: "save",
                autoUpload: false,
            },
            validation: {
                allowedExtensions: [".trdp"],
            },
            upload: (e: kendo.ui.UploadUploadEvent) => {
                if (e.sender.options.async) {
                    e.sender.options.async.saveUrl = app.webServiceBaseURL + "/api/Report/Upload/" + this.viewModel.ID();
                }
            },
            complete: (e: kendo.ui.UploadEvent) => {
                this.loadReports(this.viewModel.Name());
            },
            multiple: false,
        }).data("kendoUpload");

        me.treeView = $(me.container).find(".reportsTree").kendoTreeView({
            change(e: kendo.ui.TreeViewEvent): void {

                const dataItem = me.treeView.dataItem(me.treeView.select()) as ReportData.IReportTreeItem;
                if (dataItem && dataItem.report) {
                    me.bindReportToViewModel(dataItem.report);
                } else {
                    me.bindReportToViewModel(null);
                }
            },
            select(e: kendo.ui.TreeViewSelectEvent): void {

                if (me.haveFieldsChanged()) {
                    if (!confirm(Constants.PHRASE_ITEMUNSAVEDCHANGES)) {
                        e.preventDefault();
                        return;
                    }
                }
            },
        }).data("kendoTreeView");

        // single-click expand/collapse folders:
        $(".reportsTree").on("click", ".k-in", (e: any) => {
            me.treeView.toggle($(e.target).closest(".k-item"));
        });

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.loadReports("", deferred);

        return deferred.promise();
    }

    public appRouterID(params: any): string {
        return "Manage Reports";
    }

    public title(): string {
        return "Manage Reports";
    }

    public subTitle(): string {
        return "";
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = ko.toJSON(this.viewModel);
        return (this.viewModel.ID() !== -1 && fieldValues !== this.fieldsOnLoad)
            || this.uploadControl.getFiles().length > 0;
    }

    private loadReports(selectedItemText?: string, deferred?: JQuery.Deferred<any, any, any>) {

        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        this.bindReportToViewModel(null);

        ReportData.GetAllReports()
            .done((response: Data.IResponse<ReportData.IReportDefinition[]>) => {
                // load and display only reports that are active
                const activeReports = response.Payload.filter((report: ReportData.IReportDefinition): boolean => {
                    return (report.IsActive === true);
                });
                this.viewModel.reports(activeReports);
                this.treeDataSource = ReportData.CreateReportTreeDataSource(activeReports);
                this.treeView.setDataSource(this.treeDataSource);
                this.updateFolderList();

                if (selectedItemText) {
                    this.treeView.select(this.treeView.findByText(selectedItemText));
                }
                if (deferred) {
                    deferred.resolve();
                }
            }).always(() => {
                UI.hideWaitIndicator(this.container, waitContainer);
            });
    }

    private createTreeviewFolder(folderName: string): void {

        for (const item of this.treeDataSource) {
            if (item.text === folderName) {
                // duplicate folder, so ignore
                return;
            }
        }

        this.treeDataSource.push({
            text: folderName,
            items: [],
        });

        this.treeView.setDataSource(this.treeDataSource);
        this.updateFolderList();
    }

    private updateFolderList(): void {
        const folders: string[] = [""];
        for (const treeItem of this.treeDataSource) {
            if (!treeItem.report) {
                folders.push(treeItem.text);
            }
        }
        this.viewModel.reportFolders(folders);
    }

    private bindReportToViewModel(report: ReportData.IReportDefinition | null) {
        const app: IApplication = Global.app();

        this.uploadControl.clearAllFiles();
        if (report && report.ID > 0) {
            this.uploadControl.enable();
        } else {
            this.uploadControl.disable();
        }

        if (report === null) {
            this.viewModel.ID(-1);
            this.viewModel.Name("");
            this.viewModel.FolderName("");
            this.viewModel.FileName("");
            this.viewModel.FileKey(null);
            this.viewModel.EmailSubjectTemplate("");
            this.viewModel.reportDownloadUrl("");
        } else {
            this.viewModel.ID(report.ID);
            this.viewModel.Name(report.Name);
            this.viewModel.FolderName(report.FolderName);
            this.viewModel.FileName(report.FileName);
            this.viewModel.FileKey(report.FileKey);
            this.viewModel.EmailSubjectTemplate(report.EmailSubjectTemplate);
            this.viewModel.reportDownloadUrl(`${app.webServiceBaseURL}/api/report/download/${this.viewModel.ID()}`);
        }

        this.fieldsOnLoad = ko.toJSON(this.viewModel);

        // Prevent the validator from showing errors when switching from viewing an existing report to creating a new one
        this.viewModel.Name.isModified(false);
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/manageReports.ts